// Core lib dependencies.
import React from 'react';
import { connect } from 'react-redux';

// Component dependencies.
import PrivacySettingsButton from './PrivacySettingsButton.jsx';
import CookiesDisabledModal from './CookiesDisabledModal.jsx';
import PrivacyPopup from './PrivacyPopup.jsx';
import PrivacyQuickConsentBar from './PrivacyQuickConsentBar.jsx';

// Actions.
import Actions from '../actions.js';

// Selectors.
import Selectors from '../selectors.js';

/**
 * <pre>
 * PrivacyManagement React component.
 *
 * Determine when to display the different privacy components & display them:
 * - If current display / publishing container doesn't need privacy managment, show nothing.
 * - If user consent is needed, display the consent collection popup.
 * - If consent has previously been stored, display the privacy settings button so user can change them.
 * - If the browser has cookies disabled, show a cookies disabled modal with message.
 *
 * </pre>
 *
 * @memberof module:PrivacyManagement.Components
 * @namespace
 *
 * @return {PrivacyManagement.Components.PrivacyManagement}
 */
class PrivacyManagement extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleAcceptAll = this.handleAcceptAll.bind(this);
    this.handleSaveSelections = this.handleSaveSelections.bind(this);
    this.handleSettingsClick = this.handleSettingsClick.bind(this);
    this.handleQuickAcceptAll = this.handleQuickAcceptAll.bind(this);
    this.handleQuickEssentialOnly = this.handleQuickEssentialOnly.bind(this);
    this.handleQuickSettings = this.handleQuickSettings.bind(this);

    // Get the privacy service.
    this.privacyService = NGXD.getServiceContainer().getService('privacy');
    this.isPrivacyManagementEnabled = this.privacyService.isPrivacyManagementEnabled();
    this.isPrivacyManagementUIEnabled = this.privacyService.isPrivacyManagementUIEnabled();

    // Get Api service.
    this.apiService = NGXD.getServiceContainer().getService('api');

    // Do we need the cookies disabled modal?  Only show it when cookies are browser-disabled & a few criteria are met.
    this.isCookiesDisabledModalNeeded = this.privacyService.isCookiesDisabledModalNeeded();
    this.cookiesEnabledInBrowser = this.privacyService.doesBrowserSupportCookies();
    if (this.isCookiesDisabledModalNeeded) {
      props.dispatch.showCookiesDisabledModal();
    } else {
      props.dispatch.hideCookiesDisabledModal();

      // Do we need to show the popup? Show if there is no valid consent
      const showConsentPopup = !this.privacyService.doesValidConsentExist();
      showConsentPopup ? props.dispatch.showPrivacyPopup() : props.dispatch.hidePrivacyPopup();
    }

    // Initial state.
    this.state = {
      isFirstVisit: this.privacyService.isFirstVisit(),
    };
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:PrivacyManagement.Components.PrivacyManagement}
   */
  render() {
    // If privacyMgmt = none/disabled, we will never show the cookies disabled modal or the UI
    if (!this.isPrivacyManagementEnabled) {
      return null;
    }

    if (!this.isPrivacyManagementUIEnabled) {
      // If we don't normally need privacy management for this campaign, but cookies are disabled &&
      // our campaign really needs them, show the cookies disabled modal anyway.
      if (!this.isCookiesDisabledModalNeeded) return null;

      // else we'll show cookies disabled modal
    }

    const currentConsents = this.privacyService.get();
    return (
      <div className="xPrivacy">
        {/* If cookies are disabled completely, we may need a cookies disabled modal saying functionality will
        be broken (and how to turn on) */}
        <CookiesDisabledModal isVisible={this.props.showCookiesDisabledModal} />

        {/* If we already have a consent cookie, show a button to allow user to change choices */}
        <PrivacySettingsButton
          isVisible={this.cookiesEnabledInBrowser && !this.props.showPopup}
          onClick={this.handleSettingsClick}
        />

        {/* Show the cookie consent popup if we need the user to make a choice */}
        <PrivacyPopup
          isVisible={this.cookiesEnabledInBrowser && this.props.showPopup && !this.state.isFirstVisit}
          key={Object.values(currentConsents).join('-')}
          currentConsents={currentConsents}
          onAcceptAll={this.handleAcceptAll}
          onSave={this.handleSaveSelections}
        />

        {/* On the user's first visit, show the quick consent bar, where they can accept all easily or
          open the settings modal to set specific consents. */}
        <PrivacyQuickConsentBar
          isVisible={this.cookiesEnabledInBrowser && this.props.showPopup && this.state.isFirstVisit}
          onAcceptAll={this.handleQuickAcceptAll}
          onEssentialOnly={this.handleQuickEssentialOnly}
          onOpenSettings={this.handleQuickSettings}
        />
      </div>
    );
  }

  handleSettingsClick() {
    this.props.dispatch.showPrivacyPopup();

    // If we are showing the UI and we are iframed, the fixed positioning of the consent bar puts
    // it at the bottom of the iframe instead of the browser window, for this situation only we
    // switch the quick consent bar position to the iframe top and scroll up to it.
    //
    // The positioning is switched in the themes by looking for the xFramed class which is only
    // shown when iframed. The top and bottom position variables are simply swapped so the intended
    // height is kept but its at the top now.
    if (NGX.App.state.framed) {
      this.apiService.execute('messageParent', { action: 'getpageinfo' }, function (info) {
        NGX.App.api.scrollTo(0, (info && info.offsetTop) || 0);
      });
    }
  }

  handleQuickAcceptAll() {
    this.setState({ isFirstVisit: false });
    this.props.dispatch.hidePrivacyPopup();
  }

  handleQuickEssentialOnly() {
    this.setState({ isFirstVisit: false });
    this.props.dispatch.hidePrivacyPopup();
  }

  handleQuickSettings() {
    this.setState({ isFirstVisit: false });
    // this.props.dispatch.hidePrivacyPopup();
    this.handleSettingsClick();
  }

  handleAcceptAll() {
    this.props.dispatch.hidePrivacyPopup();
  }

  handleSaveSelections() {
    this.props.dispatch.hidePrivacyPopup();
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem being voted on.
 * @property {Object} itemOpts Object defining the various item options.
 */
PrivacyManagement.propTypes = {};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = function (state, ownProps) {
  return {
    showPopup:                Selectors.getShowPrivacyPopup(state),
    showCookiesDisabledModal: Selectors.getShowCookiesDisabledModal(state),
  };
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = function (dispatch) {
  return {
    dispatch: {
      showPrivacyPopup: function () {
        dispatch(Actions.showPrivacyPopup());
      },
      hidePrivacyPopup: function () {
        dispatch(Actions.hidePrivacyPopup());
      },

      showCookiesDisabledModal: function () {
        dispatch(Actions.showCookiesDisabledModal());
      },
      hideCookiesDisabledModal: function () {
        dispatch(Actions.hideCookiesDisabledModal());
      },
    },
  };
};

// Generate connected/smart component.
const PrivacyManagementConnected = connect(mapStateToProps, mapDispatchToProps)(PrivacyManagement);

// Export the connected/smart component as default.
export default PrivacyManagementConnected;
